import { useCallback, useEffect, type KeyboardEvent, type RefObject } from 'react';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Fora: o que está escondido e o que está dentro de [inert] (prévias decorativas), que o navegador não foca
const focusables = (root: HTMLElement) =>
    [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((x) => (x.offsetParent !== null || x === document.activeElement) && !x.closest('[inert]'));

/**
 * Foco preso num diálogo/drawer: ao abrir vai para `[data-autofocus]` (ou o 1º focável do conteúdo); Tab/Shift+Tab
 * circulam dentro; ao fechar volta para quem abriu. Devolve o `onKeyDown` do contêiner.
 */
export function useFocusTrap(open: boolean, ref: RefObject<HTMLElement | null>) {
    useEffect(() => {
        if (!open) return;
        const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const root = ref.current;
        if (root && !root.contains(document.activeElement)) {
            const target = root.querySelector<HTMLElement>('[data-autofocus]:not([disabled])') ?? focusables(root)[0] ?? root;
            target.focus();
        }
        return () => {
            if (opener && opener.isConnected) opener.focus();
        };
    }, [open, ref]);

    return useCallback((e: KeyboardEvent) => {
        const root = ref.current;
        if (e.key !== 'Tab' || !root) return;
        const items = focusables(root);
        if (!items.length) { e.preventDefault(); return; }
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && (active === first || !root.contains(active) || active === root)) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && (active === last || !root.contains(active))) { e.preventDefault(); first.focus(); }
    }, [ref]);
}
